import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { countryCodesList } from '../mock';
import { toast } from 'sonner';
import { fetchGeoLocation, submitLead, handleApiResponse } from '../services/apiService';

const UniversalPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Fetch geolocation on component mount
    const loadGeoData = async () => {
      const data = await fetchGeoLocation();
      setGeoData(data);
      if (data?.data?.countryCode) {
        const countryItem = countryCodesList.find(c => c.country === data.data.countryName);
        if (countryItem) {
          setFormData(prev => ({ ...prev, countryCode: countryItem.code }));
        }
      }
    };
    loadGeoData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitLead(formData, geoData);
      const result = handleApiResponse(response);
      
      if (result.type === 'redirect') {
        toast.success(result.message);
        navigate(result.url);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
      onClose();
      setFormData({ name: '', email: '', phone: '', countryCode: '+1', message: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col md:flex-row">
        {/* Left side - Image */}
        <div className="hidden md:block md:w-2/5 relative">
          <img 
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80" 
            alt="Contact Us" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#093336]/90 to-[#0a4548]/70 flex items-center justify-center p-8">
            <div className="text-white text-center">
              <h3 className="text-3xl font-bold mb-4">Start Your Journey</h3>
              <p className="text-gray-200 text-lg">
                Protect your brand with our expert trademark services
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-left">
                  <div className="w-12 h-12 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm">Fast & Professional Service</p>
                </div>
                <div className="flex items-center gap-3 text-left">
                  <div className="w-12 h-12 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <p className="text-sm">98% Success Rate</p>
                </div>
                <div className="flex items-center gap-3 text-left">
                  <div className="w-12 h-12 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-sm">Dedicated Case Manager</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="md:w-3/5 p-8 overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X size={24} />
          </button>
          
          <h2 className="text-3xl font-bold text-[#093336] mb-2">Get Started Today</h2>
          <p className="text-gray-600 mb-6">Fill out the form and our expert team will contact you within 24 hours.</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your.email@example.com"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
              <div className="flex gap-2">
                <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodesList.map((item) => (
                      <SelectItem key={item.id} value={item.code}>
                        {item.code} ({item.country})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    setFormData({ ...formData, phone: value });
                  }}
                  placeholder="Phone number"
                  className="flex-1"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                  maxLength={10}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your trademark needs..."
                rows={4}
                className="w-full resize-none"
              />
            </div>
            
            <Button type="submit" disabled={isSubmitting} className="w-full bg-[#093336] hover:bg-[#0a4548] text-white py-6 text-lg font-semibold disabled:opacity-50">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UniversalPopup;
